import { StarterCardChoice } from "../constants/starterCards"
import { ForeignScansMeta } from "../types/location"
import {
  AcademicLeague,
  CardElement,
  DigitalPassport,
  GameCard,
  GuardianPath,
} from "../types/battle"

export interface PassportActionResponse {
  passport: DigitalPassport
  cards: GameCard[]
}

export interface SelectLeagueRequest {
  league: AcademicLeague
  nickname?: string
  affiliationElement: CardElement
  guardianPath: GuardianPath
  starterCards: [StarterCardChoice, StarterCardChoice]
  referrerPublicGameCode?: string
}

export interface RestartGameRequest {
  affiliationElement: CardElement
  guardianPath: GuardianPath
  starterCards: [StarterCardChoice, StarterCardChoice]
}

export interface SelectLeagueResponse extends PassportActionResponse {
  leagueBalance?: import("../types/faction").LeagueBalance | null
  minorityBonusActive?: boolean
}

type GameApiErrorLike = {
  response?: { data?: unknown; status?: number; statusText?: string }
  message?: string
}

function readGameApiErrorRecord(error: unknown): Record<string, unknown> | null {
  const data = (error as GameApiErrorLike).response?.data
  if (typeof data === "string") return { message: data }
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data as Record<string, unknown>
  }
  return null
}

export function getGameApiErrorField(error: unknown, key: string): string | null {
  const record = readGameApiErrorRecord(error)
  if (!record) return null
  const snakeKey = key.replace(/[A-Z]/g, (ch) => `_${ch.toLowerCase()}`)
  for (const candidate of [key, snakeKey]) {
    const raw = record[candidate]
    if (typeof raw === "string" && raw.trim()) return raw.trim()
  }
  return null
}

function joinApiMessages(message: unknown): string | null {
  if (typeof message === "string" && message.trim()) {
    return message.trim()
  }
  if (Array.isArray(message)) {
    const parts = message
      .map((item) => (typeof item === "string" ? item.trim() : ""))
      .filter(Boolean)
    if (parts.length > 0) return parts.join(", ")
  }
  return null
}

export function isStarterCardsValidationError(message: string): boolean {
  const lower = message.toLowerCase()
  return (
    lower.includes("startercards") ||
    lower.includes("starter_cards") ||
    (lower.includes("starter") && lower.includes("must be an array"))
  )
}

/** Человекочитаемая ошибка онбординга вместо сырого class-validator. */
export function formatOnboardingApiError(
  error: unknown,
  fallback = "Не удалось создать профиль"
): string {
  const raw = getGameApiErrorMessage(error, fallback)
  if (isStarterCardsValidationError(raw)) {
    return "Сначала выберите 2 стартовые карты (стихия и стиль), затем создайте профиль."
  }
  if (isPassportExistsError(raw)) {
    return "Игровой профиль уже создан. Пробуем войти в игру…"
  }
  return raw
}

function extractHtmlPreMessage(raw: string): string | null {
  const match = raw.match(/<pre[^>]*>([\s\S]*?)<\/pre>/i)
  return match?.[1]?.trim() ?? null
}

function formatRouteNotFoundMessage(raw: string): string | null {
  if (!raw.includes("Cannot POST") && !raw.includes("Cannot PATCH")) {
    return null
  }
  if (raw.includes("/game/passport/guardian-path") || raw.includes("select-guardian-path")) {
    return "Сервер пока не поддерживает сохранение пути хранителя. Обновите приложение или обратитесь в поддержку."
  }
  return "Запрошенное действие временно недоступно на сервере."
}

export function getGameApiErrorMessage(
  error: unknown,
  fallback = "Произошла ошибка"
): string {
  const record = readGameApiErrorRecord(error)
  if (record) {
    const joined = joinApiMessages(record.message)
    if (joined) {
      if (joined.includes("<html") || joined.includes("<pre")) {
        const pre = extractHtmlPreMessage(joined)
        if (pre) {
          return formatRouteNotFoundMessage(pre) ?? pre
        }
      }
      return joined
    }
    if (typeof record.error === "string" && record.error.trim()) {
      return record.error.trim()
    }
  }

  const axiosError = error as GameApiErrorLike
  const status = axiosError.response?.status
  const responseData = axiosError.response?.data
  if (typeof responseData === "string" && responseData.trim()) {
    if (responseData.includes("<html") || responseData.includes("<pre")) {
      const pre = extractHtmlPreMessage(responseData)
      if (pre) {
        return formatRouteNotFoundMessage(pre) ?? pre
      }
    }
    return responseData.trim()
  }

  if (error instanceof Error && error.message.trim()) {
    const msg = error.message.trim()
    if (!/^Request failed with status code \d+$/.test(msg)) {
      return msg
    }
  }

  if (status) return `${fallback} (${status})`
  if (axiosError.message?.trim()) return axiosError.message
  if (axiosError.response?.statusText?.trim()) return axiosError.response.statusText

  return fallback
}

export const isCityRequiredError = (message: string): boolean =>
  message.includes("город") || message.includes("city_id")

export const isPassportExistsError = (message: string): boolean => {
  const lower = message.toLowerCase()
  return (
    message.includes("Паспорт уже создан") ||
    message.includes("уже создан") ||
    lower.includes("already exists") ||
    lower.includes("passport already")
  )
}

/** Ошибки онбординга, после которых безопасно открыть выбор карт. */
export function shouldOpenStarterCardPicker(error: unknown): boolean {
  const raw = getGameApiErrorMessage(error, "")
  return isStarterCardsValidationError(raw) || isPassportExistsError(raw)
}

export const isExamRequiredError = (message: string): boolean =>
  message.includes("Экзамен") || message.includes("Слияние")

export const isInsufficientChakraError = (message: string): boolean => {
  const lower = message.toLowerCase()
  return (
    lower.includes("недостаточно зарядов") ||
    lower.includes("недостаточно чакр") ||
    (lower.includes("недостаточно") &&
      (lower.includes("chakr") || lower.includes("чakr")))
  )
}

export const isQuizCooldownError = (message: string): boolean => {
  const lower = message.toLowerCase()
  return (
    lower.includes("завтра") ||
    lower.includes("cooldown") ||
    lower.includes("тест будет доступен") ||
    lower.includes("уже проходил")
  )
}

export const isQuizInsufficientQuestionsError = (message: string): boolean =>
  message.toLowerCase().includes("недостаточно вопросов")

export const isExamBlockedError = (message: string): boolean =>
  message.includes("заблокирован") || message.includes("blocked")

export const isLeagueRequiredError = (message: string): boolean =>
  message.includes("лигу") && message.includes("Академ")

export const isAffiliationRequiredError = (message: string): boolean =>
  message.includes("Выберите стихию хранителя") ||
  (message.toLowerCase().includes("стихию") &&
    message.toLowerCase().includes("хранител"))

export const isAffiliationAlreadySelectedError = (message: string): boolean =>
  message.includes("Стихия хранителя уже выбрана") ||
  message.toLowerCase().includes("already selected")

/** Сырой Prisma/stack trace с /game/referral/* — не показываем пользователю. */
export function formatReferralApiError(
  error: unknown,
  fallback = "Не удалось загрузить данные рефералки"
): string {
  const raw = getGameApiErrorMessage(error, fallback)
  const lower = raw.toLowerCase()

  if (
    lower.includes("prisma") ||
    lower.includes("invalid date") ||
    lower.includes("gameReferral".toLowerCase()) ||
    lower.includes("provided date object is invalid")
  ) {
    return "Статистика наград временно недоступна. Код и список друзей можно посмотреть ниже."
  }

  if (isLeagueRequiredError(raw)) {
    return "Сначала выберите лигу в Академии."
  }

  if (raw.length > 180) {
    return fallback
  }

  return raw
}

export function isReferralStatsBackendError(error: unknown): boolean {
  const raw = getGameApiErrorMessage(error, "")
  const lower = raw.toLowerCase()
  return (
    lower.includes("prisma") ||
    lower.includes("invalid date") ||
    lower.includes("gamereferral") ||
    lower.includes("provided date object is invalid")
  )
}

/** GET /game/cards без паспорта / до выбора лиги. */
export const isCardsUnavailableError = (error: unknown): boolean => {
  const axiosError = error as {
    response?: { status?: number; data?: unknown }
  }
  const status = axiosError.response?.status
  if (status === 404) return true
  if (status === 400 || status === 403) {
    const message = getGameApiErrorMessage(error, "")
    const lower = message.toLowerCase()
    return (
      isLeagueRequiredError(message) ||
      lower.includes("паспорт") ||
      lower.includes("passport") ||
      lower.includes("академ")
    )
  }
  return false
}

export const isLocationTooFarError = (message: string): boolean =>
  message.includes("далеко") || message.includes("осталось ~")

export const isLocationCooldownError = (message: string): boolean =>
  message.includes("перезарядке")

export const isLocationNotFoundError = (message: string): boolean =>
  message.includes("не найден") || message.includes("не найдена")

export const isBossNotFoundError = (message: string): boolean =>
  isLocationNotFoundError(message) || message.toLowerCase().includes("boss")

export const isForeignScanLimitError = (message: string): boolean =>
  message.toLowerCase().includes("лимит") &&
  (message.toLowerCase().includes("друг") ||
    message.toLowerCase().includes("гостев") ||
    message.toLowerCase().includes("foreign"))

export const isLocationBrokenError = (message: string): boolean =>
  message.includes("настроенных наград")

export function extractForeignScansFromError(
  error: unknown
): ForeignScansMeta | null {
  const record = readGameApiErrorRecord(error)
  if (!record) return null
  const raw = record.foreignScans ?? record.foreign_scans
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as Record<string, unknown>
  const limit = typeof item.limit === "number" ? item.limit : Number(item.limit)
  const today = typeof item.today === "number" ? item.today : Number(item.today)
  const remaining =
    typeof item.remaining === "number"
      ? item.remaining
      : Number(item.remaining)
  if (!Number.isFinite(limit) || !Number.isFinite(today)) return null
  return {
    limit,
    today,
    remaining: Number.isFinite(remaining) ? remaining : Math.max(0, limit - today),
  }
}

export const isInsufficientCoinsError = (message: string): boolean =>
  message.toLowerCase().includes("монет") ||
  message.toLowerCase().includes("coins") ||
  message.toLowerCase().includes("недостаточно")

export function getGameApiErrorCode(error: unknown): string | null {
  const record = readGameApiErrorRecord(error)
  if (!record) return null
  for (const key of ["code", "errorCode", "error_code"]) {
    const raw = record[key]
    if (typeof raw === "string" && raw.trim()) return raw.trim().toUpperCase()
  }
  return null
}

export const isBankFeeDebtError = (error: unknown, message?: string): boolean => {
  const code = getGameApiErrorCode(error)
  if (code === "BANK_FEE_DEBT") return true
  const msg = (message ?? getGameApiErrorMessage(error, "")).toLowerCase()
  return (
    msg.includes("bank_fee_debt") ||
    (msg.includes("долг") && msg.includes("банк")) ||
    (msg.includes("fee") && msg.includes("debt"))
  )
}

export const isBankFullError = (error: unknown, message?: string): boolean => {
  const code = getGameApiErrorCode(error)
  if (code === "BANK_FULL") return true
  const msg = (message ?? getGameApiErrorMessage(error, "")).toLowerCase()
  return msg.includes("bank_full") || (msg.includes("банк") && msg.includes("полон"))
}

export const isMarketLockedError = (error: unknown, message?: string): boolean => {
  const code = getGameApiErrorCode(error)
  if (code === "MARKET_LOCKED") return true
  const msg = (message ?? getGameApiErrorMessage(error, "")).toLowerCase()
  return (
    msg.includes("market_locked") ||
    (msg.includes("витрин") && msg.includes("заблок"))
  )
}

export const isHotelRestingError = (error: unknown): boolean =>
  getGameApiErrorCode(error) === "HOTEL_RESTING"

export const isHotelRestNotReadyError = (error: unknown): boolean =>
  getGameApiErrorCode(error) === "HOTEL_REST_NOT_READY"

export const isHotelAlreadyRestingError = (error: unknown): boolean =>
  getGameApiErrorCode(error) === "HOTEL_ALREADY_RESTING"

export const isAirshipGuardLockedError = (error: unknown): boolean =>
  getGameApiErrorCode(error) === "AIRSHIP_GUARD_LOCKED"

export const isAirshipRaidAlreadyError = (error: unknown): boolean =>
  getGameApiErrorCode(error) === "AIRSHIP_RAID_ALREADY"

export const isAirshipRaidDailyLimitError = (error: unknown): boolean =>
  getGameApiErrorCode(error) === "AIRSHIP_RAID_DAILY_LIMIT"

export const isBaseRaidDailyLimitError = (error: unknown): boolean => {
  const code = getGameApiErrorCode(error)
  if (code === "BASE_RAID_DAILY_LIMIT" || code === "BASE_ATTACK_DAILY_LIMIT") {
    return true
  }
  const msg = getGameApiErrorMessage(error, "").toLowerCase()
  return (
    msg.includes("лимит нападений") ||
    (msg.includes("daily") && msg.includes("attack") && msg.includes("base"))
  )
}

export const isBaseRaidCooldownError = (error: unknown): boolean => {
  const code = getGameApiErrorCode(error)
  if (code === "BASE_RAID_COOLDOWN" || code === "BASE_ATTACK_COOLDOWN") return true
  const msg = getGameApiErrorMessage(error, "").toLowerCase()
  return msg.includes("cooldown") || (msg.includes("эту базу") && msg.includes("час"))
}

export const isBaseShieldError = (error: unknown): boolean => {
  const code = getGameApiErrorCode(error)
  if (code === "BASE_SHIELD" || code === "BASE_HAS_SHIELD") return true
  const msg = getGameApiErrorMessage(error, "").toLowerCase()
  return msg.includes("щит") || msg.includes("shield")
}

export const isBaseViewportTooLargeError = (error: unknown): boolean => {
  const code = getGameApiErrorCode(error)
  if (code === "BASE_VIEWPORT_TOO_LARGE") return true
  const msg = getGameApiErrorMessage(error, "").toLowerCase()
  return (
    msg.includes("viewport") ||
    msg.includes("слишком много гекс") ||
    msg.includes("too many hex")
  )
}

export const isCityHallGuardLockedError = (error: unknown): boolean => {
  const code = getGameApiErrorCode(error)
  return (
    code === "CITY_HALL_GUARD_LOCKED" ||
    code === "CITY_RAID_ATTACK_LOCKED" ||
    code === "AIRSHIP_GUARD_LOCKED"
  )
}

export const isCityRaidAttackLockedError = (error: unknown): boolean =>
  getGameApiErrorCode(error) === "CITY_RAID_ATTACK_LOCKED"

export function getHotelRestingErrorRestEndsAt(error: unknown): string | null {
  return getGameApiErrorField(error, "restEndsAt")
}

export function getHotelRestingErrorCanCheckOut(error: unknown): boolean {
  const raw = getGameApiErrorField(error, "canCheckOut")
  return raw === "true" || raw === "1"
}

export const isCardShopLimitError = (message: string): boolean => {
  const lower = message.toLowerCase()
  return (
    lower.includes("лимит") &&
    (lower.includes("лавк") || lower.includes("покуп"))
  )
}

export const isCardShopOfferNotFoundError = (message: string): boolean => {
  const lower = message.toLowerCase()
  return lower.includes("лавк") && lower.includes("не найден")
}

export const isGuardianPathAlreadySelectedError = (message: string): boolean => {
  const lower = message.toLowerCase()
  return (
    lower.includes("сбросьте игру") ||
    lower.includes("уже выбран") ||
    lower.includes("already selected")
  )
}

export const isMailActionsLimitError = (message: string): boolean => {
  const lower = message.toLowerCase()
  return lower.includes("лимит") && lower.includes("почт")
}

export const isMailRankError = (message: string): boolean => {
  const lower = message.toLowerCase()
  return (
    lower.includes("ранг") ||
    lower.includes("rank") ||
    lower.includes("стихи")
  )
}

export const isQuestNotReadyError = (message: string): boolean =>
  message.includes("не выполн") ||
  message.includes("not completed") ||
  message.includes("already claimed") ||
  message.includes("уже получ")

/** Скан/claim прошёл, но upsert ежедневного задания упал (timezone / race на бэке). */
export const isDailyQuestSyncError = (message: string): boolean => {
  const lower = message.toLowerCase()
  return (
    lower.includes("playerdailyquest") ||
    lower.includes("player_daily_quest") ||
    lower.includes("game_player_daily_quest") ||
    (lower.includes("unique constraint") &&
      (lower.includes("questdate") || lower.includes("quest_key") || lower.includes("questkey")))
  )
}

/** Бэкенд читает completedAt как Date, хотя из SQL приходит строка. */
export const isCompletedAtSerializationError = (message: string): boolean => {
  const lower = message.toLowerCase()
  return (
    lower.includes("completedat.toisostring") ||
    lower.includes("_row_completedat")
  )
}

/** Основное действие могло пройти, но упало обновление прогресса/квестов/онбординга. */
export const isGameTrackingSyncError = (message: string): boolean =>
  isDailyQuestSyncError(message) || isCompletedAtSerializationError(message)
